import { memo, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SelectItem, SharedSelection, TmdxSelect, TmdxTextarea } from '@ds';
import { DirectAcquisitionBanner } from '@features/components/CaseCommon/DirectAcquisitionBanner';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { CaseStatus, ServiceType } from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';
import { getFilteredServiceTypes, isClinicalAuditorRestrictedToDirectAcquisition } from './utils';

type NopServiceSelectorProps = {
  disabled?: boolean;
  detailsDisabled?: boolean;
  isRequired?: boolean;
  hasPermission?: boolean;
  allowDirectAcquisition?: boolean;
  caseStatus?: CaseStatus;
  employeeRoles?: EmployeeRole[];
};

export const NopServiceSelector = memo(
  ({
    disabled,
    detailsDisabled,
    isRequired,
    hasPermission,
    allowDirectAcquisition = false,
    caseStatus,
    employeeRoles,
  }: NopServiceSelectorProps) => {
    const { setValue, trigger, control } = useFormContext<CaseFormType>();

    const selectedServiceType = useWatch({ control, name: 'serviceType' });

    const isCreateDraft = !caseStatus;

    const isClinicalAuditorRestricted = useMemo(
      () => isClinicalAuditorRestrictedToDirectAcquisition({ employeeRoles }),
      [employeeRoles],
    );

    const shouldForceDirectAcquisition = isClinicalAuditorRestricted && isCreateDraft;

    const includeDirectAcquisition = allowDirectAcquisition || shouldForceDirectAcquisition;

    const filteredServiceTypes = useMemo(
      () => getFilteredServiceTypes(includeDirectAcquisition),
      [includeDirectAcquisition],
    );

    const resetTxUsers = useCallback(() => {
      setValue('coordinatorId', '', { shouldDirty: true, shouldValidate: true });
      setValue('receivingSurgeonId', '', { shouldDirty: true, shouldValidate: true });
      setValue('isServiceWithoutCoordinator', false, { shouldDirty: true, shouldValidate: true });
    }, [setValue]);

    const triggerDirectAcquisitionFields = useCallback(() => {
      trigger('donorHospitalDate');
      trigger('donorHospitalId');
      trigger('opoId');
    }, [trigger]);

    const handleServiceTypeChange = useCallback(
      (keys: SharedSelection) => {
        const set = keys as Set<string>;
        const [next] = Array.from(set) as ServiceType[];

        setValue('serviceType', next, {
          shouldDirty: true,
          shouldValidate: true,
        });

        triggerDirectAcquisitionFields();

        if (next === ServiceType.DirectAcquisition) {
          resetTxUsers();
        }
      },
      [setValue, triggerDirectAcquisitionFields, resetTxUsers],
    );

    useEffect(() => {
      if (!shouldForceDirectAcquisition) return;

      setValue('serviceType', ServiceType.DirectAcquisition, {
        shouldDirty: false,
        shouldValidate: true,
      });

      resetTxUsers();
      triggerDirectAcquisitionFields();
    }, [shouldForceDirectAcquisition, setValue, resetTxUsers, triggerDirectAcquisitionFields]);

    return (
      <>
        <FormRow title="NOP Service" isRequired={isRequired}>
          <div className="grow-0 flex-[48.5%] w-auto">
            <Controller
              name="serviceType"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  isDisabled={disabled || !hasPermission || isClinicalAuditorRestricted}
                  placeholder="Select resource"
                  aria-label="Select resource"
                  selectedKeys={new Set(value ? [value] : [])}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  disallowEmptySelection
                  onSelectionChange={handleServiceTypeChange}
                  {...field}
                >
                  {filteredServiceTypes.map((serviceType) => (
                    <SelectItem key={serviceType.value}>{serviceType.name}</SelectItem>
                  ))}
                </TmdxSelect>
              )}
            />
          </div>
        </FormRow>

        {selectedServiceType !== ServiceType.DirectAcquisition && (
          <FormRow title="NOP Service Details" isRequired={false}>
            <Controller
              name="serviceDetails"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <TmdxTextarea
                  fullWidth
                  maxLength={128}
                  disableAnimation
                  minRows={3}
                  placeholder="Specify retrieval surgeon’s details: first and last name, collaboration type, etc."
                  isDisabled={detailsDisabled || !hasPermission}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                />
              )}
            />
          </FormRow>
        )}

        {selectedServiceType === ServiceType.DirectAcquisition && <DirectAcquisitionBanner />}
      </>
    );
  },
);
